import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { OnDestroyBase } from '@os-components';
import { SewMarkdown } from '@sew/ui-components-angular/standalone';
import { takeUntil } from 'rxjs/operators';
import { ContentType } from '../../enums/content-types.enum';
import { CustomBreakpoint, getCustomBreakpoint, mediaQueries } from '../../helpers/breakpoint.helper';
import { convertToPlainText } from '../../helpers/markdown.helper';
import { BlogPost } from '../../models/blog-posts.model';
import { ReplaceLinebreakPipe } from '../../pipes/replace-linebreak/replace-linebreak.pipe';
import { TruncatePipe } from '../../pipes/truncate/truncate.pipe';
import { DateService } from '../../shared/services/date.service';
import { TagsComponent } from '../tags/tags.component';
import { TileImageDirective } from './directives/tile-image/tile-image.directive';

@Component({
    selector: 'blog-post',
    templateUrl: './post.component.html',
    styleUrls: ['./post.component.scss'],
    animations: [
        trigger('hoverUnhover', [
            state(
                'hoverBottom',
                style({
                    bottom: '{{titleBottom}}',
                }),
                { params: { titleBottom: 'unset' } }
            ),
            state(
                'hoverTop',
                style({
                    top: '{{titleTop}}',
                }),
                { params: { titleTop: 'unset' } }
            ),
            state(
                'unhover',
                style({
                    bottom: '16px',
                })
            ),
            transition('hoverBottom => unhover', [animate('0.4s')]),
            transition('unhover => hoverBottom', [animate('0.4s')]),
            transition('hoverTop => unhover', [animate('0.4s')]),
            transition('unhover => hoverTop', [animate('0.4s')]),
            state(
                'fadeIn',
                style({
                    opacity: 1,
                })
            ),
            transition('* => fadeIn', [animate('0.4s')]),
            transition('fadeIn => *', [animate('0.4s')]),
        ]),
        trigger('zoom', [
            state(
                'zoomIn',
                style({
                    width: '*',
                    height: '*',
                    paddingTop: '56.25%',
                })
            ),
            state(
                'zoomOut',
                style({
                    width: '240px',
                    height: '135px',
                    paddingTop: '20%',
                    marginLeft: '25%',
                    marginTop: '10%',
                    marginBottom: '10%',
                })
            ),
            transition('zoomOut => zoomIn', [animate('0.4s')]),
        ]),
    ],
    imports: [
        CommonModule,
        TileImageDirective,
        TagsComponent,
        TruncatePipe,
        ReplaceLinebreakPipe,
        SewMarkdown,
    ],
})
export class PostComponent extends OnDestroyBase implements OnInit, OnChanges, OnDestroy {
    @Output() public postClicked = new EventEmitter<BlogPost>();
    @Output() public tagClicked = new EventEmitter<string>();

    @Input() public post?: BlogPost;
    @Input() public size = '';

    @HostBinding('style.width') public width?: string;
    @HostBinding('style.max-width.px') public widthMax?: number;

    public fontSizeTitle?: string;
    public isHovered = false;
    public titleTop?: string;
    public titleBottom?: string;
    public textHover = '';
    public tags: string[] = [];
    public breakpoint: CustomBreakpoint = 0;
    public backgroundImg?: string;
    public zoomState = 'zoomOut';
    public state = '';
    public authorName?: string;
    public date?: string;

    public constructor(
        private breakpointObserver: BreakpointObserver,
        private dateService: DateService
    ) {
        super();
    }

    @HostListener('mouseenter')
    public onMouseEnter() {
        this.isHovered = true;
    }

    @HostListener('mouseleave')
    public onMouseLeave() {
        this.isHovered = false;
    }

    public ngOnInit(): void {
        if (!this.post) {
            return;
        }

        this.init(this.post);
        setTimeout(() => {
            this.zoomState = 'zoomIn';
        }, 100);

        this.initBreakpointListener();
    }

    public ngOnChanges(): void {
        if (!this.post) {
            return;
        }

        this.init(this.post);
    }

    public onTagClicked(tag: string): void {
        this.tagClicked.emit(tag);
    }

    public showArticle(): void {
        this.postClicked.emit(this.post);
    }

    private init(post: BlogPost): void {
        this.tags = post.tags;
        this.titleBottom = '16px';
        this.date = this.dateService.getDate(post.date);
        if (post.author) {
            this.authorName = post.author.firstname + ' ' + post.author.surname;
        }

        switch (this.size) {
            case 's': {
                this.titleTop = '29px';
                this.state = 'hoverTop';
                this.fontSizeTitle = '24px';
                break;
            }
            case 'm': {
                this.titleBottom = '151px';
                this.state = 'hoverBottom';
                this.fontSizeTitle = '24px';
                break;
            }
            case 'l': {
                this.titleBottom = '147px';
                this.state = 'hoverBottom';
                this.fontSizeTitle = '44px';
                break;
            }
            default: {
                this.titleBottom = '147px';
                this.state = 'hoverBottom';
                this.width = '100%';
                this.fontSizeTitle = '44px';
                break;
            }
        }

        if (post.content && post.content.length) {
            for (const content of post.content) {
                if (content.type === ContentType.TEXT) {
                    this.textHover = convertToPlainText(content.text);
                    break;
                }
            }
        }
    }

    private initBreakpointListener(): void {
        this.breakpointObserver
            .observe([mediaQueries.mq1, mediaQueries.mq2, mediaQueries.mq3])
            .pipe(takeUntil(this.destroySubject))
            .subscribe((bpState) => {
                this.breakpoint = getCustomBreakpoint(bpState);
            });
    }
}
